import { type Timestamp, timestampDate } from '@bufbuild/protobuf/wkt'
import type { ColDef, ValueGetterParams } from 'ag-grid-community'
import _ from 'lodash'

import { EMPTY_FILTER_VALUE } from '@/components/lemmala-design-system/table/consts'

/**
 * Utility functions for working with AG Grid column definitions.
 * These helpers provide common operations needed across different table implementations.
 */

/**
 * Maps a column ID to its field name.
 * Handles the colId vs field distinction in AG Grid column definitions.
 */
export const getColumnField = <TRowData = unknown>(columnDef: ColDef<TRowData>): string => {
  return columnDef.colId || columnDef.field || ''
}

/**
 * Maps a column ID to its display name (headerName).
 * Useful for showing human-readable names in filter triggers and other UI elements.
 */
export const getColumnHeaderName = <TRowData = unknown>(columnDef: ColDef<TRowData>): string => {
  return columnDef.headerName || getColumnField(columnDef)
}

/**
 * Extracts filterable columns from a list of column definitions.
 * A column is considered filterable if it has a filter property defined.
 */
export const getFilterableColumns = <TRowData = unknown>(
  columnDefs: ColDef<TRowData>[],
): ColDef<TRowData>[] => {
  return columnDefs.filter((col) => {
    // Column is filterable if it has a filter property (even if false)
    return col.filter !== undefined && col.filter !== false
  })
}

/**
 * Gets filterable column IDs based on an optional selection list.
 * This is useful for view-specific filter bars that only show certain filters.
 */
export const getFilterableColumnIds = <TRowData = unknown>(
  columnDefs: ColDef<TRowData>[],
  selectedColIds?: string[],
): string[] => {
  const filterableColumns = getFilterableColumns(columnDefs)
  const filterableIds = filterableColumns.map(getColumnField).filter(Boolean)

  // If no selection provided, return all filterable column IDs
  if (!selectedColIds || selectedColIds.length === 0) {
    return filterableIds
  }

  // Return only the selected IDs that are actually filterable
  return selectedColIds.filter((id) => filterableIds.includes(id))
}

/**
 * Finds a column definition by its column ID or field name.
 */
export const findColumnByField = <TRowData = unknown>(
  columnDefs: ColDef<TRowData>[],
  fieldOrColId: string,
): ColDef<TRowData> | undefined => {
  return columnDefs.find((col) => {
    const colField = getColumnField(col)
    return colField === fieldOrColId || col.colId === fieldOrColId
  })
}

/**
 * Gets the filter value getter function for a column.
 * This is used to extract the correct values for set filters from complex data structures.
 */
export const getColumnFilterValueGetter = <TRowData = unknown>(columnDef: ColDef<TRowData>) => {
  // Check if column has a custom filterValueGetter
  if (columnDef.filterValueGetter) {
    return columnDef.filterValueGetter
  }

  // Check if filter params have a valueGetter
  if (columnDef.filterParams && 'valueGetter' in columnDef.filterParams) {
    return (
      columnDef.filterParams as {
        valueGetter: (params: { data: TRowData }) => unknown
      }
    ).valueGetter
  }

  // Fall back to the column's regular valueGetter
  if (columnDef.valueGetter) {
    return columnDef.valueGetter
  }

  // Default: extract from field name
  return (params: { data: TRowData }) => {
    const field = getColumnField(columnDef)
    return params.data ? (params.data as Record<string, unknown>)[field] : undefined
  }
}

export type AgGridRowDateValue = Date | Timestamp | null

const isTimestampLike = (value: AgGridRowDateValue): value is Timestamp => {
  return typeof value === 'object' && value !== null && 'seconds' in value
}

export const normalizeAgGridDateValue = (value: AgGridRowDateValue): Date | null => {
  if (value == null) return null

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (isTimestampLike(value)) {
    try {
      const parsedDate = timestampDate(value)
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
    } catch {
      return null
    }
  }

  return null
}

export const parseAgGridFilterModelDate = (value: string | null | undefined): Date | null => {
  if (!value) return null

  const parsedDate = new Date(value)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

export const agGridDateFilterComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: AgGridRowDateValue,
) => {
  const cellDate = normalizeAgGridDateValue(cellValue)
  if (!cellDate) return -1

  return cellDate.getTime() - filterLocalDateAtMidnight.getTime()
}

// Compares by calendar day, so a range ending today keeps the rows from later today.
export const agGridDayFilterComparator = (
  filterLocalDateAtMidnight: Date,
  cellValue: AgGridRowDateValue,
) => {
  const cellDate = normalizeAgGridDateValue(cellValue)
  if (!cellDate) return -1

  const cellDay = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate())
  return cellDay.getTime() - filterLocalDateAtMidnight.getTime()
}

/**
 * Checks if a column is hidden based on its hide property.
 * Useful for view management systems.
 */
export const isColumnHidden = <TRowData = unknown>(columnDef: ColDef<TRowData>): boolean => {
  return columnDef.hide === true
}

/**
 * Gets the display name for filter trigger buttons.
 * Uses headerName if available, falls back to field name with proper formatting.
 */
export const getFilterDisplayName = <TRowData = unknown>(columnDef: ColDef<TRowData>): string => {
  const headerName = getColumnHeaderName(columnDef)

  // If we have a proper header name, use it
  if (headerName && headerName !== getColumnField(columnDef)) {
    return headerName
  }

  // Otherwise, format the field name nicely
  const field = getColumnField(columnDef)
  return field
    .split(/[._-]/) // Split on common separators
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Creates a filler column that flexes to fill remaining horizontal space.
 * - No header, no content, not resizable/movable/sortable, not exported
 */
export const createFillerColumn = <TRowData = unknown>(
  colId: string = '__filler__',
): ColDef<TRowData> => {
  return {
    colId,
    headerName: '',
    valueGetter: () => '',
    flex: 1,
    minWidth: 0,
    resizable: false,
    suppressMovable: true,
    sortable: false,
    filter: false,
    menuTabs: [],
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressCsvExport: true,
  } as ColDef<TRowData>
}

/**
 * Adds the "(empty)" option to an array of filter values for AG Grid set filters.
 * This allows users to filter for rows that have empty/null values for the column.
 *
 * @param values - Array of filter values (can be any type)
 * @returns Array with "(empty)" prepended and duplicates removed
 *
 * @example
 * ```ts
 * filterParams: {
 *   values: withEmptyOption(['Option 1', 'Option 2'])
 *   // Results in: ['(empty)', 'Option 1', 'Option 2']
 * }
 * ```
 */
export const withEmptyOption = <T>(values: T[]): (string | T)[] => {
  return values.length ? _.uniq([EMPTY_FILTER_VALUE, ...values]) : []
}

/**
 * Returns unique string values, ensuring at least "(empty)" is present if the array is empty.
 * Used in AG Grid set filter valueGetter functions to handle empty/null values.
 *
 * @param values - Array of string values (may be empty)
 * @returns Array with unique values, or ['(empty)'] if input is empty
 *
 * @example
 * ```ts
 * valueGetter: (params) => {
 *   const names = params.data?.tags?.map(t => t.name) || []
 *   return uniqueStringsOrEmpty(names)
 * }
 * ```
 */
export const uniqueStringsOrEmpty = (values: string[]): string[] => {
  return _.uniq(values.length === 0 ? [EMPTY_FILTER_VALUE] : values)
}

/**
 * Extracts a column's value from row data outside of AG Grid's runtime.
 * Checks filterValueGetter → valueGetter → field path, in that order.
 */
export function extractColumnValue<T>(col: ColDef<T>, row: T, baseParams?: ValueGetterParams<T>) {
  // AG Grid doesn't expose a standalone value extractor — we build the params
  // ourselves, keeping baseParams (e.g. `context`) when called from inside the grid.
  const params = { ...baseParams, data: row } as ValueGetterParams<T>

  if (col.filterValueGetter && typeof col.filterValueGetter !== 'string') {
    return col.filterValueGetter(params)
  }
  if (col.valueGetter && typeof col.valueGetter !== 'string') {
    return col.valueGetter(params)
  }
  if (col.field) {
    return _.get(row, col.field)
  }
  return undefined
}
